import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, GripVertical, Loader2, Minus, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { JapaneseVehiclePlatePopoverInput } from '@/components/efs/JapaneseVehiclePlatePopoverInput';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useIncomingItemsList } from '@/features/masters/incoming-items/useIncomingItems';
import { useSuppliersList } from '@/features/masters/suppliers/useSuppliers';
import { EVIDENCE_MEDIA_ACCEPT, uploadEvidence } from '@/features/evidence/api';
import {
  EVIDENCE_MEDIA_MIME_GROUPS,
  FileLibraryPickerDialog,
  fileMatchesEvidenceMediaAccept,
} from '@/features/file-library';
import { useFileLibraryFeatureEnabled } from '@/features/file-library/useFileLibraryFeature';
import type { FileLibrarySelection, FileSourceMode } from '@/features/file-library/types';
import { useCurrentUser } from '@/lib/auth-context';
import { apiClient, type ApiSuccess } from '@/lib/api-client';
import { hasPermission } from '@/lib/permissions';
import { localizeApiError } from '@/features/audit-issues';

import {
  inboundLotWithPackagesCreateSchema,
  type CreateInboundLotWithPackagesResponse,
  type InboundLotWithPackagesInput,
  type RequiredTruckPhotoKind,
  requiredTruckPhotoKindValues,
} from './types';
import { useCreateInboundLotWithPackages } from './useInbound';
import { TruckPhotoDirectionZone } from './TruckPhotoDirectionZone';
import { WeightTicketEntryCard } from './WeightTicketEntryCard';
import { attachInboundEvidenceFromLibrary } from './api';

const NS = 'operations';
type CreateStep = 1 | 2;
type SiteEvidenceSelection =
  | { source: 'local'; file: File }
  | { source: 'library'; file: FileLibrarySelection };
type SiteEvidenceFiles = Partial<Record<RequiredTruckPhotoKind, SiteEvidenceSelection>>;
type SiteEvidenceErrors = Partial<Record<RequiredTruckPhotoKind, string>>;

/**
 * One quick-add row aggregated by incoming_item_id. Each row expands into
 * `qty` package objects on submit so the backend creates one inbound_package
 * per physical bag/box. Operators tap a chip to increment qty, or use the
 * +/- buttons in the row table.
 */
interface QuickRow {
  incoming_item_id: string;
  japanese_name: string;
  qty: number;
}

interface MonthlyBatchForInboundCreate {
  monthly_batch_id: string;
  year_month: string;
}

const monthStart = (yearMonth: string | undefined): string | undefined =>
  yearMonth && /^\d{4}-\d{2}$/.test(yearMonth) ? `${yearMonth}-01` : undefined;

const monthEnd = (yearMonth: string | undefined): string | undefined => {
  if (!yearMonth || !/^\d{4}-\d{2}$/.test(yearMonth)) return undefined;
  const [year, month] = yearMonth.split('-').map(Number);
  return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
};

const isInYearMonth = (date: string | undefined, yearMonth: string | undefined): boolean =>
  !!date && !!yearMonth && date.startsWith(`${yearMonth}-`);

interface InboundCreateDialogProps {
  batchId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  dispatchRequestId?: string;
  defaults?: Partial<
    Pick<InboundLotWithPackagesInput, 'inbound_date' | 'source_company_id' | 'vehicle' | 'notes'>
  >;
  navigateOnCreated?: boolean;
  onCreated?: (created: CreateInboundLotWithPackagesResponse) => void | Promise<void>;
}

export const InboundCreateDialog = ({
  batchId,
  open,
  onOpenChange,
  dispatchRequestId,
  defaults,
  navigateOnCreated = true,
  onCreated,
}: InboundCreateDialogProps) => {
  const { t } = useTranslation([NS, 'issues']);
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const create = useCreateInboundLotWithPackages();
  const fileLibraryEnabled = useFileLibraryFeatureEnabled();

  const itemsQuery = useIncomingItemsList({
    status: 'approved',
    active: true,
    per_page: 500,
  });
  const items = itemsQuery.data?.rows ?? [];

  const suppliersQuery = useSuppliersList({ active_only: true, per_page: 500 });
  const suppliers = suppliersQuery.data?.rows ?? [];
  const batchQuery = useQuery({
    queryKey: ['monthly-batches', 'detail', batchId],
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<MonthlyBatchForInboundCreate>>(
        `/monthly-batches/${batchId}`,
      );
      return res.data.data;
    },
    enabled: open && !!batchId,
  });
  const batchYearMonth = batchQuery.data?.year_month;
  const batchMinDate = monthStart(batchYearMonth);
  const batchMaxDate = monthEnd(batchYearMonth);

  const [rows, setRows] = useState<QuickRow[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [draggingItemId, setDraggingItemId] = useState<string | null>(null);
  const [step, setStep] = useState<CreateStep>(1);
  const [siteEvidenceFiles, setSiteEvidenceFiles] = useState<SiteEvidenceFiles>({});
  const [siteEvidenceErrors, setSiteEvidenceErrors] = useState<SiteEvidenceErrors>({});
  const [siteEvidenceRequiredError, setSiteEvidenceRequiredError] = useState<string | null>(null);
  const [siteEvidenceUploadingKind, setSiteEvidenceUploadingKind] =
    useState<RequiredTruckPhotoKind | null>(null);
  const [siteEvidenceSourceModes, setSiteEvidenceSourceModes] = useState<
    Record<RequiredTruckPhotoKind, FileSourceMode>
  >({ left: 'local', right: 'local', rear: 'local' });
  const [libraryPickerKind, setLibraryPickerKind] = useState<RequiredTruckPhotoKind | null>(null);
  const [vehicleEditorOpen, setVehicleEditorOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const resetKeyRef = useRef<string | null>(null);
  const localizedMutationError = (
    error: unknown,
    fallbackKey: string,
    defaultValue: string,
  ): string => {
    const text = localizeApiError((key, opts) => t(key, { ns: 'issues', ...(opts ?? {}) }), error);
    return text.cataloged ? text.message : t(fallbackKey, { defaultValue });
  };

  const initialDefaults = useCallback(
    (): InboundLotWithPackagesInput => ({
      lot_code: '',
      inbound_date: defaults?.inbound_date ?? '',
      source_company_id: defaults?.source_company_id ?? null,
      vehicle: defaults?.vehicle ?? '',
      notes: defaults?.notes ?? '',
      measured_net_kg: '',
      declared_pallet_count: '',
      declared_fureikon_count: '',
      declared_cardboard_count: '',
      arrival_at: '',
      departure_at: '',
      truck_photo_evidence_file_ids: [],
      truck_photo_kinds: {},
      packages: [],
    }),
    [defaults?.inbound_date, defaults?.notes, defaults?.source_company_id, defaults?.vehicle],
  );

  const form = useForm<InboundLotWithPackagesInput>({
    resolver: zodResolver(inboundLotWithPackagesCreateSchema),
    defaultValues: initialDefaults(),
  });

  // Reset only when a new dialog instance/batch/default identity opens. The
  // async monthly-batch query must not wipe media, rows, or step after the user
  // has already started entering data.
  useEffect(() => {
    if (!open) {
      resetKeyRef.current = null;
      return;
    }
    const resetKey = JSON.stringify({
      batchId,
      dispatchRequestId: dispatchRequestId ?? '',
      inbound_date: defaults?.inbound_date ?? '',
      source_company_id: defaults?.source_company_id ?? '',
      vehicle: defaults?.vehicle ?? '',
      notes: defaults?.notes ?? '',
    });
    if (resetKeyRef.current === resetKey) return;
    resetKeyRef.current = resetKey;
    form.reset(initialDefaults());
    setRows([]);
    setDraggingItemId(null);
    setStep(1);
    setSiteEvidenceFiles({});
    setSiteEvidenceErrors({});
    setSiteEvidenceRequiredError(null);
    setSiteEvidenceUploadingKind(null);
    setSiteEvidenceSourceModes({ left: 'local', right: 'local', rear: 'local' });
    setLibraryPickerKind(null);
    setVehicleEditorOpen(false);
    setSubmitError(null);
  }, [
    batchId,
    dispatchRequestId,
    defaults?.inbound_date,
    defaults?.notes,
    defaults?.source_company_id,
    defaults?.vehicle,
    form,
    initialDefaults,
    open,
  ]);

  const totalPackages = rows.reduce((sum, r) => sum + r.qty, 0);
  const missingPackages = totalPackages < 1;
  const hasInvalidQty = rows.some((r) => r.qty < 1);
  const selectedSiteEvidenceEntries = requiredTruckPhotoKindValues.flatMap((kind) => {
    const selection = siteEvidenceFiles[kind];
    return selection ? [{ kind, selection }] : [];
  });
  const findDuplicateSiteEvidenceSelection = () => {
    const seen = new Map<string, RequiredTruckPhotoKind>();
    for (const { kind, selection } of selectedSiteEvidenceEntries) {
      const key =
        selection.source === 'library'
          ? `library:${selection.file.company_file_id}`
          : `local:${[
              selection.file.name,
              selection.file.size,
              selection.file.lastModified,
              selection.file.type,
            ].join(':')}`;
      const firstKind = seen.get(key);
      if (firstKind) return { firstKind, duplicateKind: kind };
      seen.set(key, kind);
    }
    return null;
  };

  // Truck-arrival worksheet — declared totals must equal the chip-grid total
  // when the operator filled them in. Empty / zero = "skipped, no audit".
  const watchedPallet = Number(form.watch('declared_pallet_count') ?? '') || 0;
  const watchedFureikon = Number(form.watch('declared_fureikon_count') ?? '') || 0;
  const watchedCardboard = Number(form.watch('declared_cardboard_count') ?? '') || 0;
  const declaredTotal = watchedPallet + watchedFureikon + watchedCardboard;
  const declaredAuditMismatch = declaredTotal > 0 && declaredTotal !== totalPackages;

  const isBusy = create.isPending || isSubmitting;
  const canUseFileLibrary =
    fileLibraryEnabled &&
    (hasPermission(currentUser, 'file_library.read') ||
      hasPermission(currentUser, 'file_library.manage'));
  const watchedInboundDate = form.watch('inbound_date');
  const batchMonthBlocked =
    batchQuery.isLoading || batchQuery.isError || !batchMinDate || !batchMaxDate;
  const inboundDateOutsideBatch =
    !!batchYearMonth && !!watchedInboundDate && !isInYearMonth(watchedInboundDate, batchYearMonth);
  const batchMonthMessage = batchQuery.isError
    ? t('efs.operations.inbound.create.batch_month_load_error', {
        defaultValue: '対象月を取得できないため、入荷日は登録できません。',
      })
    : batchQuery.isLoading || !batchMinDate || !batchMaxDate
      ? t('efs.operations.inbound.create.batch_month_loading', {
          defaultValue: '対象月を確認中です。',
        })
      : null;

  useEffect(() => {
    if (!batchYearMonth || !batchMinDate) return;
    const current = form.getValues('inbound_date');
    if (!current || !isInYearMonth(current, batchYearMonth)) {
      form.setValue('inbound_date', batchMinDate, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true,
      });
      form.clearErrors('inbound_date');
    }
  }, [batchMinDate, batchYearMonth, form]);

  const handleSiteEvidenceChange = (kind: RequiredTruckPhotoKind, files: FileList) => {
    const file = files[0];
    if (!file) return;
    setSiteEvidenceFiles((current) => ({ ...current, [kind]: { source: 'local', file } }));
    setSiteEvidenceErrors((current) => {
      const next = { ...current };
      delete next[kind];
      return next;
    });
    setSiteEvidenceRequiredError(null);
    setSubmitError(null);
  };

  const handleSiteEvidenceLibrarySelect = (
    kind: RequiredTruckPhotoKind,
    file: FileLibrarySelection,
  ) => {
    setSiteEvidenceFiles((current) => ({ ...current, [kind]: { source: 'library', file } }));
    setSiteEvidenceErrors((current) => {
      const next = { ...current };
      delete next[kind];
      return next;
    });
    setSiteEvidenceRequiredError(null);
    setSubmitError(null);
  };

  const removeSiteEvidence = (kind: RequiredTruckPhotoKind) => {
    setSiteEvidenceFiles((current) => {
      const next = { ...current };
      delete next[kind];
      return next;
    });
    setSiteEvidenceErrors((current) => {
      const next = { ...current };
      delete next[kind];
      return next;
    });
  };

  const goToStep2 = async () => {
    setSubmitError(null);
    if (batchMonthBlocked) {
      setSubmitError(batchMonthMessage);
      return;
    }
    const ok = await form.trigger(['inbound_date']);
    if (!ok) return;
    if (inboundDateOutsideBatch) {
      form.setError('inbound_date', {
        type: 'batch-month',
        message: t('efs.operations.inbound.create.inbound_date_month_error', {
          month: batchYearMonth,
          defaultValue: '入荷日は対象月 {{month}} の範囲内で選択してください',
        }),
      });
      return;
    }
    if (selectedSiteEvidenceEntries.length === 0) {
      setSiteEvidenceRequiredError(
        t('efs.operations.inbound.create.step1_media_required', {
          defaultValue: '写真または動画を1件選択してください',
        }),
      );
      return;
    }
    const duplicateSelection = findDuplicateSiteEvidenceSelection();
    if (duplicateSelection) {
      setSiteEvidenceRequiredError(
        t('efs.operations.inbound.create.step1_media_duplicate', {
          first: t(`efs.operations.inbound.truck_photos.kind.${duplicateSelection.firstKind}`, {
            defaultValue: duplicateSelection.firstKind,
          }),
          duplicate: t(
            `efs.operations.inbound.truck_photos.kind.${duplicateSelection.duplicateKind}`,
            { defaultValue: duplicateSelection.duplicateKind },
          ),
          defaultValue:
            '{{first}} と {{duplicate}} に同じ写真/動画が選択されています。別のファイルを選択してください。',
        }),
      );
      return;
    }
    setStep(2);
  };

  const handleAddItem = (incoming_item_id: string, japanese_name: string) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.incoming_item_id === incoming_item_id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        return next;
      }
      return [...prev, { incoming_item_id, japanese_name, qty: 1 }];
    });
  };

  const handleQtyDelta = (incoming_item_id: string, delta: number) => {
    setRows((prev) =>
      prev.map((r) =>
        r.incoming_item_id === incoming_item_id ? { ...r, qty: Math.max(1, r.qty + delta) } : r,
      ),
    );
  };

  const handleRemoveRow = (incoming_item_id: string) => {
    setRows((prev) => prev.filter((r) => r.incoming_item_id !== incoming_item_id));
  };

  const handleDropRow = (targetItemId: string) => {
    if (!draggingItemId || draggingItemId === targetItemId) return;
    setRows((prev) => {
      const from = prev.findIndex((r) => r.incoming_item_id === draggingItemId);
      const to = prev.findIndex((r) => r.incoming_item_id === targetItemId);
      if (from < 0 || to < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const onSubmit = form.handleSubmit(async (values) => {
    if (selectedSiteEvidenceEntries.length === 0) {
      setStep(1);
      setSiteEvidenceRequiredError(
        t('efs.operations.inbound.create.step1_media_required', {
          defaultValue: '写真または動画を1件選択してください',
        }),
      );
      return;
    }
    const duplicateSelection = findDuplicateSiteEvidenceSelection();
    if (duplicateSelection) {
      setStep(1);
      setSiteEvidenceRequiredError(
        t('efs.operations.inbound.create.step1_media_duplicate', {
          first: t(`efs.operations.inbound.truck_photos.kind.${duplicateSelection.firstKind}`, {
            defaultValue: duplicateSelection.firstKind,
          }),
          duplicate: t(
            `efs.operations.inbound.truck_photos.kind.${duplicateSelection.duplicateKind}`,
            { defaultValue: duplicateSelection.duplicateKind },
          ),
          defaultValue:
            '{{first}} と {{duplicate}} に同じ写真/動画が選択されています。別のファイルを選択してください。',
        }),
      );
      return;
    }
    if (batchMonthBlocked || inboundDateOutsideBatch) {
      setStep(1);
      if (batchMonthBlocked) {
        setSubmitError(batchMonthMessage);
      }
      if (inboundDateOutsideBatch) {
        form.setError('inbound_date', {
          type: 'batch-month',
          message: t('efs.operations.inbound.create.inbound_date_month_error', {
            month: batchYearMonth,
            defaultValue: '入荷日は対象月 {{month}} の範囲内で選択してください',
          }),
        });
      }
      return;
    }
    // Expand quick-add rows into N package objects per row.
    const packages = rows.flatMap((r) =>
      Array.from({ length: r.qty }, () => ({
        incoming_item_id: r.incoming_item_id,
        raw_incoming_item_name: r.japanese_name,
      })),
    );
    const toStr = (s: string | undefined) => (s && s.trim() !== '' ? s.trim() : undefined);
    const toInt = (s: string | undefined) => {
      const trimmed = s?.trim();
      if (!trimmed) return undefined;
      const n = Number(trimmed);
      return Number.isFinite(n) ? Math.trunc(n) : undefined;
    };
    // `datetime-local` (local, minute precision) → ISO-8601 for the wire.
    const toIso = (s: string | undefined) => {
      const trimmed = s?.trim();
      if (!trimmed) return undefined;
      const d = new Date(trimmed);
      return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
    };
    // Backend expects Decimal (accepts string) for measured_net_kg and i32
    // (must be a JSON number) for the three declared counts. We send `any`
    // here because the zod schema models the form-input strings, not the
    // wire shape.
    setIsSubmitting(true);
    setSiteEvidenceErrors({});
    setSiteEvidenceRequiredError(null);
    setSubmitError(null);
    try {
      const uploadedEvidence: Array<{ evidenceFileId: string; kind: RequiredTruckPhotoKind }> = [];
      for (const { kind, selection } of selectedSiteEvidenceEntries) {
        setSiteEvidenceUploadingKind(kind);
        try {
          const evidence =
            selection.source === 'local'
              ? await uploadEvidence(batchId, selection.file, 'photo')
              : await attachInboundEvidenceFromLibrary(
                  batchId,
                  selection.file.company_file_id,
                  'photo',
                );
          uploadedEvidence.push({ evidenceFileId: evidence.evidence_file_id, kind });
        } catch (err) {
          setStep(1);
          setSiteEvidenceErrors((current) => ({
            ...current,
            [kind]: localizedMutationError(
              err,
              'efs.operations.inbound.errors.truck_photo_upload_failed',
              'トラック写真を追加できませんでした。時間をおいて再試行してください。',
            ),
          }));
          throw err;
        }
      }
      const body = {
        lot_code: '', // server assigns canonical code; this remains trace-only.
        ...(dispatchRequestId ? { dispatch_request_id: dispatchRequestId } : {}),
        inbound_date: values.inbound_date,
        source_company_id: values.source_company_id?.trim()
          ? values.source_company_id.trim()
          : null,
        vehicle: values.vehicle?.trim() ? values.vehicle.trim() : null,
        notes: values.notes?.trim() ? values.notes.trim() : null,
        measured_net_kg: toStr(values.measured_net_kg),
        declared_pallet_count: toInt(values.declared_pallet_count),
        declared_fureikon_count: toInt(values.declared_fureikon_count),
        declared_cardboard_count: toInt(values.declared_cardboard_count),
        arrival_at: toIso(values.arrival_at),
        departure_at: toIso(values.departure_at),
        truck_photo_evidence_file_ids: uploadedEvidence.map(({ evidenceFileId }) => evidenceFileId),
        truck_photo_kinds: Object.fromEntries(
          uploadedEvidence.map(({ evidenceFileId, kind }) => [evidenceFileId, kind]),
        ),
        packages,
      } as unknown as InboundLotWithPackagesInput;
      const created = await create.mutateAsync({ batchId, body });
      await onCreated?.(created);
      onOpenChange(false);
      if (navigateOnCreated) {
        navigate(`/batches/${batchId}/inbound/${created.inbound_lot_id}`);
      }
    } catch (err) {
      setSubmitError(
        localizedMutationError(
          err,
          'efs.operations.inbound.errors.create_lot_failed',
          '入荷ロットを作成できませんでした。入力内容を確認して再試行してください。',
        ),
      );
    } finally {
      setSiteEvidenceUploadingKind(null);
      setIsSubmitting(false);
    }
  });

  const submitDisabled =
    isBusy ||
    selectedSiteEvidenceEntries.length === 0 ||
    batchMonthBlocked ||
    inboundDateOutsideBatch ||
    missingPackages ||
    hasInvalidQty ||
    declaredAuditMismatch;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-3xl overflow-y-auto"
        onEscapeKeyDown={(event) => {
          if (vehicleEditorOpen) {
            event.preventDefault();
            setVehicleEditorOpen(false);
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('efs.operations.inbound.create.title')}</DialogTitle>
          <DialogDescription>{t('efs.operations.inbound.create.subtitle')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-5" noValidate>
            <div className="flex items-center gap-2 rounded-md bg-efs-bg-subtle p-1 text-sm">
              <span
                className={
                  'rounded px-3 py-1 ' +
                  (step === 1 ? 'bg-efs-bg-panel font-semibold text-efs-text-primary' : '')
                }
              >
                {t('efs.operations.inbound.create.step1', {
                  defaultValue: 'Step 1 進貨工作單',
                })}
              </span>
              <span
                className={
                  'rounded px-3 py-1 ' +
                  (step === 2 ? 'bg-efs-bg-panel font-semibold text-efs-text-primary' : '')
                }
              >
                {t('efs.operations.inbound.create.step2', {
                  defaultValue: 'Step 2 品名・包装',
                })}
              </span>
            </div>

            {step === 1 ? (
              <>
                {/* Lot header */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="inbound_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('efs.operations.inbound.fields.inbound_date')}</FormLabel>
                        <FormControl>
                          <DatePicker
                            value={field.value ?? ''}
                            onChange={(value) => {
                              field.onChange(value);
                              if (!batchYearMonth || isInYearMonth(value, batchYearMonth)) {
                                form.clearErrors('inbound_date');
                              }
                            }}
                            minDate={batchMinDate}
                            maxDate={batchMaxDate}
                            disabled={batchMonthBlocked || isBusy}
                          />
                        </FormControl>
                        {batchMonthMessage ? (
                          <p
                            className={
                              'text-xs ' +
                              (batchQuery.isError ? 'text-red-900' : 'text-efs-text-muted')
                            }
                          >
                            {batchMonthMessage}
                          </p>
                        ) : null}
                        {batchYearMonth ? (
                          <p className="text-xs text-efs-text-muted">
                            {t('efs.operations.inbound.create.inbound_date_month_hint', {
                              month: batchYearMonth,
                              defaultValue: '対象月 {{month}} の日付のみ登録できます。',
                            })}
                          </p>
                        ) : null}
                        {inboundDateOutsideBatch ? (
                          <p className="text-xs text-red-900">
                            {t('efs.operations.inbound.create.inbound_date_month_error', {
                              month: batchYearMonth,
                              defaultValue: '入荷日は対象月 {{month}} の範囲内で選択してください',
                            })}
                          </p>
                        ) : null}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* lot_code is assigned by the backend; no canonical input here. */}
                  <FormField
                    control={form.control}
                    name="source_company_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('efs.operations.inbound.fields.source_company')}</FormLabel>
                        <FormControl>
                          <Select
                            value={field.value ?? ''}
                            onValueChange={(v) => field.onChange(v === '' ? null : v)}
                          >
                            <SelectTrigger>
                              <SelectValue
                                placeholder={t(
                                  'efs.operations.inbound.fields.source_company_placeholder',
                                )}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {suppliers.map((s) => (
                                <SelectItem key={s.company_id} value={s.company_id}>
                                  {s.display_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem className="sm:col-span-2">
                        <FormLabel>{t('efs.operations.inbound.fields_lot.notes')}</FormLabel>
                        <FormControl>
                          <Textarea rows={2} {...field} value={field.value ?? ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <WeightTicketEntryCard
                  title={t('efs.operations.inbound.worksheet.ticket.title')}
                  hint={t('efs.operations.inbound.worksheet.ticket.hint')}
                  timeLabel={t('efs.operations.inbound.worksheet.ticket.time')}
                  vehicleLabel={t('efs.operations.inbound.worksheet.ticket.vehicle')}
                  grossLabel={t('efs.operations.inbound.worksheet.ticket.gross')}
                  tareLabel={t('efs.operations.inbound.worksheet.ticket.tare')}
                  netLabel={t('efs.operations.inbound.worksheet.ticket.net')}
                  blankValueLabel={t('efs.operations.inbound.worksheet.ticket.blank_value')}
                  timeField={
                    <FormField
                      control={form.control}
                      name="arrival_at"
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <FormLabel className="sr-only">
                            {t('efs.operations.inbound.worksheet.ticket.time')}
                          </FormLabel>
                          <FormControl>
                            <Input type="datetime-local" {...field} value={field.value ?? ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  }
                  vehicleField={
                    <FormField
                      control={form.control}
                      name="vehicle"
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <FormLabel className="sr-only">
                            {t('efs.operations.inbound.worksheet.ticket.vehicle')}
                          </FormLabel>
                          <FormControl>
                            <JapaneseVehiclePlatePopoverInput
                              id="inbound-create-vehicle"
                              ref={field.ref}
                              value={field.value ?? ''}
                              open={vehicleEditorOpen}
                              onOpenChange={setVehicleEditorOpen}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  }
                  netField={
                    <FormField
                      control={form.control}
                      name="measured_net_kg"
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <FormLabel className="sr-only">
                            {t('efs.operations.inbound.worksheet.ticket.net')}
                          </FormLabel>
                          <div className="relative">
                            <FormControl>
                              <Input
                                type="number"
                                inputMode="decimal"
                                step="0.1"
                                min={0}
                                placeholder="8520"
                                className="pr-10 text-base font-semibold tabular-nums"
                                {...field}
                                value={field.value ?? ''}
                              />
                            </FormControl>
                            <span
                              className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-efs-text-muted"
                              aria-hidden="true"
                            >
                              kg
                            </span>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  }
                />

                {/* Secondary worksheet fields are not printed in the weigh-ticket block. */}
                <div className="rounded-md border border-efs-border bg-efs-bg-panel p-3 sm:p-4">
                  <p className="text-sm font-semibold">
                    {t('efs.operations.inbound.worksheet.ticket.secondary_title')}
                  </p>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="departure_at"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t('efs.operations.inbound.worksheet.departure_at')}
                          </FormLabel>
                          <FormControl>
                            <Input type="datetime-local" {...field} value={field.value ?? ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="declared_pallet_count"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t('efs.operations.inbound.worksheet.pallet_count')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              inputMode="numeric"
                              step={1}
                              min={0}
                              placeholder="11"
                              {...field}
                              value={field.value ?? ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="declared_fureikon_count"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t('efs.operations.inbound.worksheet.fureikon_count')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              inputMode="numeric"
                              step={1}
                              min={0}
                              placeholder="3"
                              {...field}
                              value={field.value ?? ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="declared_cardboard_count"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t('efs.operations.inbound.worksheet.cardboard_count')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              inputMode="numeric"
                              step={1}
                              min={0}
                              placeholder="0"
                              {...field}
                              value={field.value ?? ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <fieldset className="rounded-md border border-efs-border bg-efs-bg-panel p-3 sm:p-4">
                  <legend className="px-1 text-sm font-semibold">
                    {t('efs.operations.inbound.create.step1_media_label', {
                      defaultValue: '現場写真・動画',
                    })}
                    <span className="ml-1 text-red-900">*</span>
                  </legend>
                  <p className="mb-3 text-xs text-efs-text-muted">
                    {t('efs.operations.inbound.create.step1_media_hint', {
                      defaultValue:
                        '方向ごとの枠から写真または動画を選択してください。作成時は1件以上、確定時は左側・右側・後方が各1件必要です。',
                    })}
                  </p>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {requiredTruckPhotoKindValues.map((kind) => {
                      const selection = siteEvidenceFiles[kind];
                      const kindLabel = t(`efs.operations.inbound.truck_photos.kind.${kind}`, {
                        defaultValue: kind,
                      });
                      const uploading = siteEvidenceUploadingKind === kind;
                      const uploadLabel = t(
                        selection
                          ? 'efs.operations.inbound.truck_photos.replace_for_kind'
                          : 'efs.operations.inbound.truck_photos.add_for_kind',
                        {
                          kind: kindLabel,
                          defaultValue: selection
                            ? '{{kind}}の写真・動画を変更'
                            : '{{kind}}の写真・動画を選択',
                        },
                      );
                      return (
                        <TruckPhotoDirectionZone
                          key={kind}
                          title={kindLabel}
                          statusLabel={
                            uploading
                              ? t('efs.operations.inbound.truck_photos.status_uploading', {
                                  defaultValue: 'アップロード中…',
                                })
                              : selection
                                ? t('efs.operations.inbound.truck_photos.status_selected', {
                                    defaultValue: '選択済み',
                                  })
                                : t('efs.operations.inbound.truck_photos.status_missing', {
                                    defaultValue: '未登録',
                                  })
                          }
                          hasContent={!!selection}
                          uploading={uploading}
                          canUpload={!isBusy}
                          sourceMode={siteEvidenceSourceModes[kind]}
                          canUseLibrary={canUseFileLibrary}
                          libraryLabel={t('efs.file_library.picker.title', {
                            ns: 'fileLibrary',
                            defaultValue: 'ファイル庫から選択',
                          })}
                          librarySelectedLabel={
                            selection?.source === 'library' ? selection.file.display_name : null
                          }
                          onSourceModeChange={(mode) =>
                            setSiteEvidenceSourceModes((current) => ({
                              ...current,
                              [kind]: mode,
                            }))
                          }
                          onLibrarySelectClick={() => setLibraryPickerKind(kind)}
                          uploadLabel={uploadLabel}
                          inputLabel={uploadLabel}
                          accept={EVIDENCE_MEDIA_ACCEPT}
                          error={siteEvidenceErrors[kind]}
                          onFilesSelected={(files) => handleSiteEvidenceChange(kind, files)}
                        >
                          {selection ? (
                            <div className="flex items-start justify-between gap-2 text-xs">
                              <span className="min-w-0 break-all text-efs-text-muted">
                                {selection.source === 'local'
                                  ? selection.file.name
                                  : selection.file.display_name}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 shrink-0"
                                onClick={() => removeSiteEvidence(kind)}
                                disabled={isBusy}
                                aria-label={t(
                                  'efs.operations.inbound.truck_photos.remove_for_kind',
                                  {
                                    kind: kindLabel,
                                    defaultValue: '{{kind}}の選択を解除',
                                  },
                                )}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <p className="text-xs text-efs-text-muted">
                              {t('efs.operations.inbound.truck_photos.direction_empty_hint', {
                                kind: kindLabel,
                                defaultValue: '{{kind}}が写る写真を選択してください。',
                              })}
                            </p>
                          )}
                        </TruckPhotoDirectionZone>
                      );
                    })}
                  </div>
                  <FileLibraryPickerDialog
                    open={!!libraryPickerKind}
                    onOpenChange={(nextOpen) => {
                      if (!nextOpen) setLibraryPickerKind(null);
                    }}
                    batchId={batchId}
                    acceptMimeGroups={EVIDENCE_MEDIA_MIME_GROUPS}
                    acceptFile={fileMatchesEvidenceMediaAccept}
                    onSelect={(file) => {
                      if (libraryPickerKind) {
                        handleSiteEvidenceLibrarySelect(libraryPickerKind, file);
                      }
                    }}
                  />
                  {siteEvidenceRequiredError ? (
                    <p role="alert" className="mt-2 text-xs text-red-900 dark:text-red-100">
                      {siteEvidenceRequiredError}
                    </p>
                  ) : null}
                </fieldset>
              </>
            ) : (
              <>
                {declaredTotal > 0 ? (
                  <div
                    className={
                      'rounded-md px-3 py-2 text-xs ' +
                      (declaredAuditMismatch
                        ? 'bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100'
                        : 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100')
                    }
                  >
                    {t('efs.operations.inbound.worksheet.audit', {
                      declared: declaredTotal,
                      actual: totalPackages,
                      diff: declaredTotal - totalPackages,
                    })}
                  </div>
                ) : null}

                {/* Quick-add chip grid */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold">
                    {t('efs.operations.inbound.quick_add.label')}
                  </label>
                  <p className="text-xs text-efs-text-muted">
                    {t('efs.operations.inbound.quick_add.hint')}
                  </p>
                  {itemsQuery.isLoading ? (
                    <p className="text-xs text-efs-text-muted">
                      {t('efs.common.messages.loading', { ns: 'common' })}
                    </p>
                  ) : items.length === 0 ? (
                    <p className="rounded-md border border-dashed border-efs-border p-3 text-xs text-efs-text-muted">
                      {t('efs.operations.inbound.quick_add.no_items')}
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                      {items.map((it) => (
                        <Button
                          key={it.incoming_item_id}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddItem(it.incoming_item_id, it.japanese_name)}
                          className="h-auto justify-start whitespace-normal px-3 py-2 text-left text-xs"
                        >
                          <Plus className="mr-1 h-3 w-3 shrink-0" />
                          <span className="truncate">{it.japanese_name}</span>
                        </Button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Rows table */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">
                      {t('efs.operations.inbound.package_section')}
                    </span>
                    <span className="font-mono text-xs">
                      {t('efs.operations.inbound.quick_add.total_packages', {
                        count: totalPackages,
                        defaultValue: '{{count}}',
                      })}
                    </span>
                  </div>
                  <div className="rounded-md border border-efs-border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            {t('efs.operations.inbound.quick_add.row_table.row_no')}
                          </TableHead>
                          <TableHead>
                            {t('efs.operations.inbound.quick_add.row_table.item')}
                          </TableHead>
                          <TableHead className="w-20 text-right">
                            {t('efs.operations.inbound.quick_add.row_table.qty')}
                          </TableHead>
                          <TableHead className="w-32 text-center">
                            {t('efs.operations.inbound.quick_add.row_table.adjust')}
                          </TableHead>
                          <TableHead className="w-16 text-right">
                            {t('efs.operations.inbound.quick_add.row_table.remove')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              className="py-6 text-center text-xs text-efs-text-muted"
                            >
                              {t('efs.operations.inbound.quick_add.empty_hint')}
                            </TableCell>
                          </TableRow>
                        ) : (
                          rows.map((r, idx) => (
                            <TableRow
                              key={r.incoming_item_id}
                              draggable
                              className={
                                draggingItemId === r.incoming_item_id
                                  ? 'cursor-grabbing opacity-60'
                                  : 'cursor-grab'
                              }
                              onDragStart={() => setDraggingItemId(r.incoming_item_id)}
                              onDragOver={(event) => event.preventDefault()}
                              onDrop={(event) => {
                                event.preventDefault();
                                handleDropRow(r.incoming_item_id);
                                setDraggingItemId(null);
                              }}
                              onDragEnd={() => setDraggingItemId(null)}
                            >
                              <TableCell className="font-mono text-xs">
                                <span className="inline-flex items-center gap-1">
                                  <GripVertical
                                    className="h-3.5 w-3.5 text-efs-text-muted"
                                    aria-hidden="true"
                                  />
                                  <span
                                    title={t(
                                      'efs.operations.inbound.quick_add.row_table.drag_to_reorder',
                                    )}
                                  >
                                    {idx + 1}
                                  </span>
                                </span>
                              </TableCell>
                              <TableCell className="text-xs">{r.japanese_name}</TableCell>
                              <TableCell className="text-right font-mono text-sm font-semibold">
                                {r.qty}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => handleQtyDelta(r.incoming_item_id, -1)}
                                    disabled={r.qty <= 1}
                                    aria-label={t(
                                      'efs.operations.inbound.quick_add.row_table.minus',
                                    )}
                                  >
                                    <Minus className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => handleQtyDelta(r.incoming_item_id, 1)}
                                    aria-label={t(
                                      'efs.operations.inbound.quick_add.row_table.plus',
                                    )}
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-efs-text-muted hover:text-red-900"
                                  onClick={() => handleRemoveRow(r.incoming_item_id)}
                                  aria-label={t(
                                    'efs.operations.inbound.quick_add.row_table.remove',
                                  )}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            )}

            {submitError ? <p className="text-xs text-red-900">{submitError}</p> : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isBusy}
              >
                {t('efs.common.actions.cancel', { ns: 'common' })}
              </Button>
              {step === 2 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  disabled={isBusy}
                >
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  {t('efs.operations.inbound.actions.back_to_step1', {
                    defaultValue: '工作單に戻る',
                  })}
                </Button>
              ) : null}
              {step === 1 ? (
                <Button
                  type="button"
                  onClick={() => void goToStep2()}
                  disabled={isBusy || batchMonthBlocked}
                >
                  {t('efs.operations.inbound.actions.next_step')}
                </Button>
              ) : (
                <Button type="submit" disabled={submitDisabled}>
                  {isSubmitting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                  {t('efs.operations.inbound.actions.create_lot', {
                    defaultValue: '入荷ロット作成',
                  })}
                </Button>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default InboundCreateDialog;
